import React from "react";
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { mediaUrl } from "../utils/mediaUrl";

// Allowed HTML tags/attributes for checkout rich-text content.
// Scripts and event handlers are always stripped.
const purify = (html) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span", "div", "h1", "h2", "h3", "h4", "img"],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "style"],
    ADD_ATTR: ["target"],
    FORCE_BODY: true,
  });
import {
  Check,
  CheckCircle,
  X,
  Circle,
  CircleDot,
  Dot,
  ChevronRight,
  ArrowRight,
  Sparkles,
  XCircleIcon,
} from "lucide-react";

// Hook to get user city
const useUserCity = () => {
  const [city, setCity] = useState(null);

  useEffect(() => {
    // Check if we already have city in sessionStorage
    const cachedCity = sessionStorage.getItem("userCity");
    if (cachedCity) {
      setCity(cachedCity);
      return;
    }

    // Fetch city from API
    const fetchCity = async () => {
      try {
        const response = await fetch("https://wtfismyip.com/json");
        if (response.ok) {
          const data = await response.json();
          // Extract city name from YourFuckingLocation
          const cityName = data.YourFuckingLocation?.replace(/\,.+/g, "") || "";
          setCity(cityName);
          sessionStorage.setItem("userCity", cityName);
        }
      } catch (error) {
        console.error("Error fetching city:", error);
        setCity("");
      }
    };

    fetchCity();
  }, []);

  return city || "";
};

// Component for random decreasing countdown
const RandomDecreasingCounter = ({ start, end, minutes }) => {
  const [currentValue, setCurrentValue] = useState(start);
  const startTimeRef = React.useRef(Date.now());
  const totalDuration = minutes * 60 * 1000;
  const totalDecrease = start - end;

  useEffect(() => {
    if (currentValue <= end) return;

    const elapsed = Date.now() - startTimeRef.current;
    const progress = Math.min(elapsed / totalDuration, 1);
    const remainingTime = totalDuration - elapsed;

    if (remainingTime <= 0) {
      setCurrentValue(end);
      return;
    }

    const expectedValue = Math.floor(start - totalDecrease * progress);
    const isAhead = currentValue < expectedValue;

    let minInterval = isAhead ? 6000 : 3000;
    let maxInterval = isAhead ? 9000 : 6000;

    if (remainingTime < 30000) {
      const stepsNeeded = currentValue - end;
      if (stepsNeeded > 0) {
        const avgIntervalNeeded = remainingTime / stepsNeeded;
        minInterval = Math.max(1000, avgIntervalNeeded * 0.8);
        maxInterval = Math.min(9000, avgIntervalNeeded * 1.2);
      }
    }

    const randomInterval =
      Math.floor(Math.random() * (maxInterval - minInterval + 1)) + minInterval;

    const timeout = setTimeout(() => {
      const decrease = Math.floor(Math.random() * 2) + 1;
      const newValue = Math.max(end, currentValue - decrease);
      setCurrentValue(newValue);
    }, randomInterval);

    return () => clearTimeout(timeout);
  }, [currentValue, start, end, totalDuration, totalDecrease]);

  return <>{currentValue}</>;
};

// Component for countdown timer
const CountdownDisplay = ({ minutes, seconds }) => {
  const [timeLeft, setTimeLeft] = useState(minutes * 60 + seconds);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  const displayMinutes = Math.floor(timeLeft / 60);
  const displaySeconds = timeLeft % 60;

  return (
    <>
      {displayMinutes.toString().padStart(2, "0")}:
      {displaySeconds.toString().padStart(2, "0")}
    </>
  );
};

// Simple atomitag replacement for non-component tags
const replaceAtomitags = (text, replacements) => {
  let result = text;
  Object.entries(replacements).forEach(([tag, value]) => {
    result = result.replace(new RegExp(`\\${tag}`, "g"), value);
  });
  return result;
};

// Process text with atomitags
const processTextWithAtomitags = (text, atomitags) => {
  if (!text) return "";

  // First replace simple atomitags
  let processedText = text;
  Object.entries(atomitags).forEach(([tag, value]) => {
    if (tag !== "$aleatorio_decrescente" && tag !== "$countdown") {
      processedText = processedText.replace(new RegExp(`\\${tag}`, "g"), value);
    }
  });

  // Handle countdown tags
  const countdownParts = [];
  let countdownLastIndex = 0;
  const countdownRegex = /\$countdown-(\d{1,2}):(\d{2})/g;
  let countdownMatch;

  while ((countdownMatch = countdownRegex.exec(processedText)) !== null) {
    if (countdownMatch.index > countdownLastIndex) {
      countdownParts.push(
        processedText.substring(countdownLastIndex, countdownMatch.index)
      );
    }

    const minutes = parseInt(countdownMatch[1]);
    const seconds = parseInt(countdownMatch[2]);

    countdownParts.push(
      <CountdownDisplay
        key={`countdown-${countdownMatch.index}`}
        minutes={minutes}
        seconds={seconds}
      />
    );

    countdownLastIndex = countdownMatch.index + countdownMatch[0].length;
  }

  if (countdownLastIndex < processedText.length) {
    countdownParts.push(processedText.substring(countdownLastIndex));
  }

  // Process the result for aleatorio_decrescente
  const processForAleatorio = (input) => {
    if (typeof input === "string") {
      const parts = [];
      let lastIndex = 0;
      const regex = /\$aleatorio_decrescente=(\d+)>(\d+)\((\d+)\)/g;
      let match;

      while ((match = regex.exec(input)) !== null) {
        if (match.index > lastIndex) {
          parts.push(input.substring(lastIndex, match.index));
        }

        const start = parseInt(match[1]);
        const end = parseInt(match[2]);
        const minutes = parseInt(match[3]);

        parts.push(
          <RandomDecreasingCounter
            key={`rdc-${match.index}`}
            start={start}
            end={end}
            minutes={minutes}
          />
        );

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < input.length) {
        parts.push(input.substring(lastIndex));
      }

      return parts.length === 0 ? input : parts;
    }
    return input;
  };

  // Process countdown parts for aleatorio_decrescente
  if (countdownParts.length > 0) {
    const finalParts = [];
    countdownParts.forEach((part) => {
      if (typeof part === "string") {
        const processed = processForAleatorio(part);
        if (Array.isArray(processed)) {
          finalParts.push(...processed);
        } else {
          finalParts.push(processed);
        }
      } else {
        finalParts.push(part);
      }
    });
    return finalParts.length === 1 && typeof finalParts[0] === "string"
      ? finalParts[0]
      : finalParts;
  }

  // Just process for aleatorio_decrescente
  const result = processForAleatorio(processedText);
  return Array.isArray(result) &&
    result.length === 1 &&
    typeof result[0] === "string"
    ? result[0]
    : result;
};

// Component to render individual checkout design components
export const ComponentRenderer = ({
  component,
  allComponents,
  isMobile,
  theme,
}) => {
  // Find child components
  const childComponents = allComponents.filter(
    (c) => c.parentId === component.id
  );

  // Get user city for atomitag replacement
  const userCity = useUserCity();

  switch (component.type) {
    case "container": {
      // Map align values to valid Tailwind classes
      const alignMap = {
        "flex-start": "items-start",
        start: "items-start",
        center: "items-center",
        "flex-end": "items-end",
        end: "items-end",
        stretch: "items-stretch",
        baseline: "items-baseline",
      };

      const alignClass = component.content?.align
        ? alignMap[component.content.align] || ""
        : "";
      return (
        <div
          className={cn(
            "flex",
            component.content?.direction === "row" ? "flex-row" : "flex-col",
            alignClass,
            component.content?.flex && "flex-1"
          )}
          style={{
            gap: component.content?.gap
              ? `${component.content.gap}px`
              : undefined,
            padding: component.content?.padding
              ? `${component.content.padding}px`
              : undefined,
          }}
        >
          {childComponents.map((child) => (
            <ComponentRenderer
              key={child.id}
              component={child}
              allComponents={allComponents}
              isMobile={isMobile}
              theme={theme}
            />
          ))}
        </div>
      );
    }

    case "text": {
      const fontSize = {
        sm: "text-sm",
        base: "text-base",
        lg: "text-lg",
        xl: "text-xl",
        "2xl": "text-2xl",
      }[component.content?.fontSize || "base"];

      const textAlign = {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      }[component.content?.align || "left"];

      // Font size in rem for style
      const fontSizeMap = {
        sm: "0.875rem", // 14px
        base: "1rem", // 16px
        lg: "1.125rem", // 18px
        xl: "1.25rem", // 20px
        "2xl": "1.5rem", // 24px
      };
      const fontSizeStyle = fontSizeMap[component.content?.fontSize || "base"];

      // Use theme body text color by default
      const textColor =
        component.content?.color || theme?.bodyText || "#374151";

      // Prepare atomitag replacements
      const atomitags = {
        $cidade: userCity,
        $hoje: (() => {
          const hoje = new Date();
          const dia = hoje.getDate().toString().padStart(2, "0");
          const mes = (hoje.getMonth() + 1).toString().padStart(2, "0");
          const ano = hoje.getFullYear();
          return `${dia}/${mes}/${ano}`;
        })(),
      };

      if (component.content?.html) {
        // Handle HTML content
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = component.content.html;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        const processedContent = processTextWithAtomitags(
          textContent,
          atomitags
        );

        // If processedContent is an array (contains React components), render differently
        if (Array.isArray(processedContent)) {
          return (
            <div
              style={{
                fontSize: fontSizeStyle,
                textAlign: component.content?.align || "left",
                color: textColor,
              }}
              className={cn("rich-content space-y-1", fontSize, textAlign)}
            >
              {processedContent}
            </div>
          );
        }

        // Otherwise, use dangerouslySetInnerHTML
        const processedHtml = replaceAtomitags(
          component.content.html,
          atomitags
        );
        return (
          <div
            style={{
              fontSize: fontSizeStyle,
              textAlign: component.content?.align || "left",
              color: textColor,
            }}
            className={cn("rich-content space-y-1", fontSize, textAlign)}
            dangerouslySetInnerHTML={{ __html: purify(processedHtml) }}
          />
        );
      }

      const text = component.content?.text || "Seu texto aqui";
      const processedContent = processTextWithAtomitags(text, atomitags);

      // If processedContent contains React components
      if (Array.isArray(processedContent)) {
        return (
          <div
            style={{
              fontSize: fontSizeStyle,
              textAlign: component.content?.align || "left",
              color: textColor,
            }}
          >
            {processedContent.map((item, idx) => {
              if (typeof item === "string") {
                const lines = item.split("\n");
                return lines.map((line, lineIdx) => (
                  <p
                    key={`${idx}-${lineIdx}`}
                    className={cn("leading-snug", fontSize, textAlign)}
                  >
                    {line}
                  </p>
                ));
              }
              return <span key={idx}>{item}</span>;
            })}
          </div>
        );
      }

      // Otherwise, render as simple text
      const lines = processedContent.split("\n");
      return (
        <div
          style={{
            fontSize: fontSizeStyle,
            textAlign: component.content?.align || "left",
            color: textColor,
          }}
        >
          {lines.map((line, idx) => (
            <p key={idx} className={cn("leading-snug", fontSize, textAlign)}>
              {line}
            </p>
          ))}
        </div>
      );
    }

    case "image": {
      const url =
        mediaUrl(component.content?.url) ||
        "https://assets.atomicpay.net/public/1-1-placeholder.png";
      const widthMode = component.content?.widthMode || "fixed";
      const width = component.content?.width || 320;
      const align = component.content?.align || "center";
      const borderRadius = component.content?.borderRadius ?? 0;
      const shadow = component.content?.shadow || "none";

      // Shadow styles
      const shadowStyles = {
        none: "none",
        subtle: "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px",
        small:
          "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
        medium:
          "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
        large:
          "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      };

      const imgStyles = {
        width: widthMode === "full" ? "100%" : `${width}px`,
        maxWidth: "100%",
        borderRadius: `${borderRadius}px`,
        boxShadow: shadowStyles[shadow],
      };

      const wrapperStyles = {
        display: "flex",
        justifyContent:
          align === "left"
            ? "flex-start"
            : align === "right"
            ? "flex-end"
            : "center",
        width: widthMode === "full" ? "100%" : "auto",
      };

      return (
        <div style={wrapperStyles}>
          <img
            src={url}
            alt={component.content?.alt || "imagem"}
            className="h-auto"
            style={imgStyles}
          />
        </div>
      );
    }

    case "testimonial": {
      const bgColor = component.content?.bgColor || "#fff";
      const textColor = component.content?.textColor || "#000";
      const name = component.content?.name || "João da Silva";
      const text =
        component.content?.text || "Esse é o melhor curso que já comprei!";
      const rating = component.content?.rating || 4;
      const imageUrl = mediaUrl(component.content?.imageUrl) || null;
      const horizontal = component.content?.horizontal || false;
      const size = component.content?.size || "medium";
      const bgMode = component.content?.bgMode || "none";

      // Sizing map
      const sizeMap = {
        small: {
          img: 48,
          font: 14,
          stars: 18,
          author: 12,
          padding: 8,
          minHeight: 80,
        },
        medium: {
          img: 80,
          font: 18,
          stars: 28,
          author: 14,
          padding: 16,
          minHeight: 120,
        },
      };
      const sz = sizeMap[size] || sizeMap.medium;

      // Professional shadow presets (same as benefits)
      const shadowStyles = {
        none: "none",
        subtle: "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px",
        small:
          "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
        medium:
          "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
        large:
          "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
        glow: `rgba(167, 230, 0, 0.4) 0px 0px 20px 0px`, // Using default glow color
      };

      const shadow = shadowStyles[component.content?.shadow || "none"];

      // Build styles object
      let wrapperStyles = {
        color: textColor,
        maxWidth: "100%",
        minWidth: 0,
        minHeight: sz.minHeight,
        padding: `${component.content?.padding ?? sz.padding}px`,
        flexWrap: horizontal ? "wrap" : undefined,
        boxShadow: shadow,
        borderRadius: `${component.content?.borderRadius ?? 12}px`,
        width: component.content?.width
          ? `${component.content.width}px`
          : "100%",
      };

      // Apply background based on bgMode
      if (bgMode === "solid") {
        wrapperStyles.backgroundColor = bgColor;
      } else if (bgMode === "gradient") {
        const start = component.content?.gradientStartColor || "#FFFFFF";
        const end = component.content?.gradientEndColor || "#F3F4F6";
        wrapperStyles.backgroundImage = `linear-gradient(135deg, ${start}, ${end})`;
      } else {
        wrapperStyles.backgroundColor = "transparent";
      }

      return (
        <div
          className={cn(
            "rounded-xl border border-gray-200 flex",
            horizontal
              ? "flex-row items-center gap-8 flex-wrap"
              : "flex-col items-center text-center gap-2"
          )}
          style={wrapperStyles}
        >
          {/* Avatar */}
          <div
            className={cn(
              "flex-shrink-0 flex items-center justify-center",
              horizontal ? "mr-6" : ""
            )}
            style={{ width: sz.img, height: sz.img }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className="rounded-full object-cover border border-gray-200"
                style={{ width: sz.img, height: sz.img }}
              />
            ) : (
              <svg
                width={sz.img}
                height={sz.img}
                viewBox="0 0 80 80"
                fill="none"
              >
                <circle cx="40" cy="40" r="40" fill="#E5E7EB" />
                <circle cx="40" cy="34" r="14" fill="#D1D5DB" />
                <path
                  d="M40 52c-11 0-20 5-20 11v3h40v-3c0-6-9-11-20-11z"
                  fill="#D1D5DB"
                />
              </svg>
            )}
          </div>
          {/* Content */}
          <div
            className={cn(
              "flex flex-col items-center",
              horizontal ? "items-start flex-1" : ""
            )}
            style={{ flex: 1 }}
          >
            <div
              className="font-semibold mb-2"
              style={{ color: textColor, fontSize: sz.font }}
            >
              {text}
            </div>
            <div className="flex gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  width={sz.stars}
                  height={sz.stars}
                  viewBox="0 0 24 24"
                  fill={star <= rating ? "#FFD600" : "#E5E7EB"}
                  stroke={star <= rating ? "#FFD600" : "#E5E7EB"}
                  strokeWidth="1.5"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </div>
            <div
              className="text-base text-gray-700 mt-1"
              style={{ color: textColor, opacity: 0.8, fontSize: sz.author }}
            >
              {name}
            </div>
          </div>
        </div>
      );
    }

    case "button":
      return (
        <button
          className={cn(
            "px-4 py-2 rounded-md font-medium transition-colors",
            component.content?.variant === "primary"
              ? "bg-[#A7E600] hover:bg-[#DAFF50] text-black"
              : "bg-gray-200 hover:bg-gray-300 text-gray-800"
          )}
          style={{
            fontSize: component.content?.fontSize,
          }}
        >
          {component.content?.text || "Clique aqui"}
        </button>
      );

    case "divider":
      return (
        <hr
          className="my-4"
          style={{
            borderColor: component.content?.color || "#e5e7eb",
            borderWidth: component.content?.thickness || 1,
          }}
        />
      );

    case "spacer":
      return (
        <div
          style={{
            height: component.content?.height || 20,
          }}
        />
      );

    case "timer":
      return <CountdownTimer {...component.content} />;

    case "video":
      if (!component.content?.url) return null;

      return (
        <div className="relative w-full aspect-video">
          <iframe
            src={component.content.url}
            className="absolute inset-0 w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );

    case "faq":
      return (
        <div className="space-y-3">
          {(component.content?.items || []).map((item, index) => (
            <details
              key={index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <summary className="font-medium cursor-pointer">
                {item.question}
              </summary>
              <p className="mt-2 text-gray-600">{item.answer}</p>
            </details>
          ))}
        </div>
      );

    case "benefits": {
      // Prepare atomitag replacements
      const atomitags = {
        $cidade: userCity,
        $hoje: (() => {
          const hoje = new Date();
          const dia = hoje.getDate().toString().padStart(2, "0");
          const mes = (hoje.getMonth() + 1).toString().padStart(2, "0");
          const ano = hoje.getFullYear();
          return `${dia}/${mes}/${ano}`;
        })(),
      };

      const iconKey = component.content?.icon || "check";
      const iconMap = {
        play: (props) => (
          <svg {...props} fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        ),
        at: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
            />
          </svg>
        ),
        check: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        users: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        chart: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        ),
        chat: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        ),
        cloud: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        ),
        folder: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        ),
        sparkle: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
        ),
        download: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>
        ),
        globe: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        heart: (props) => (
          <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        ),
      };

      const IconComponent = iconMap[iconKey] || iconMap.check;
      const primary = component.content?.colorPrimary || "#1d4fd7";
      const primaryAlpha =
        (component.content?.colorPrimaryOpacity ?? 100) / 100;
      const sizeSm = component.content?.size === "small";
      const bgMode =
        component.content?.bgMode ||
        (component.content?.bgColor &&
        component.content.bgColor !== "transparent"
          ? "solid"
          : "none");
      const vertical = component.content?.vertical;
      const titleHtml = component.content?.titleHtml || "Título";
      const descHtml = component.content?.descHtml || "Descrição";
      const align = component.content?.align || "left";
      const textColor = component.content?.textColor || "#000000";

      // Process title
      const tempTitleDiv = document.createElement("div");
      tempTitleDiv.innerHTML = titleHtml;
      const titleText = tempTitleDiv.textContent || "";
      const processedTitle = processTextWithAtomitags(titleText, atomitags);

      // Process description
      const tempDescDiv = document.createElement("div");
      tempDescDiv.innerHTML = descHtml;
      const descText = tempDescDiv.textContent || "";
      const processedDesc = processTextWithAtomitags(descText, atomitags);

      // function to convert hex to rgba string
      const hexToRgba = (hex, alpha) => {
        const h = hex.replace("#", "");
        const bigint = parseInt(h, 16);
        const r = (bigint >> 16) & 255,
          g = (bigint >> 8) & 255,
          b = bigint & 255;
        return `rgba(${r},${g},${b},${alpha})`;
      };

      // Professional shadow presets
      const shadowStyles = {
        none: "none",
        subtle: "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px",
        small:
          "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
        medium:
          "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
        large:
          "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
        glow: `${hexToRgba(primary, 0.4)} 0px 0px 20px 0px`,
      };

      const shadow = shadowStyles[component.content?.shadow || "none"];

      // Build background styles
      let backgroundStyles = {
        borderWidth: component.content?.borderWidth || 0,
        borderStyle: component.content?.borderType || "solid",
        borderColor: component.content?.borderColor || "#E5E7EB",
        borderRadius: `${component.content?.borderRadius ?? 8}px`,
        padding: `${component.content?.padding || 16}px`,
        boxShadow: shadow,
      };

      if (bgMode === "solid") {
        backgroundStyles.backgroundColor =
          component.content?.bgColor || "#FFFFFF";
      } else if (bgMode === "gradient") {
        const start = component.content?.gradientStartColor || "#FFFFFF";
        const end = component.content?.gradientEndColor || "#F3F4F6";
        backgroundStyles.backgroundImage = `linear-gradient(135deg, ${start}, ${end})`;
      } else {
        backgroundStyles.backgroundColor = "transparent";
      }

      return (
        <div
          className={cn("rounded-lg", {
            "text-left": align === "left",
            "text-center": align === "center",
            "text-right": align === "right",
          })}
          style={backgroundStyles}
        >
          <div
            className={cn(
              "flex gap-4",
              vertical ? "flex-col items-center" : "items-start",
              {
                "justify-start": !vertical && align === "left",
                "justify-center": !vertical && align === "center",
                "justify-end": !vertical && align === "right",
              }
            )}
          >
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                backgroundColor: hexToRgba(primary, primaryAlpha),
                width: sizeSm ? 32 : 48,
                height: sizeSm ? 32 : 48,
              }}
            >
              <IconComponent
                className="text-white"
                style={{ width: sizeSm ? 16 : 24, height: sizeSm ? 16 : 24 }}
              />
            </div>
            <div
              className={cn("flex-1", vertical && "text-center")}
              style={{ minWidth: 0 }}
            >
              <div
                className={cn(
                  "font-semibold",
                  sizeSm ? "text-sm" : "text-base"
                )}
                style={{ color: textColor }}
              >
                {Array.isArray(processedTitle) ? (
                  processedTitle
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: purify(replaceAtomitags(titleHtml, atomitags)),
                    }}
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      hyphens: "auto",
                    }}
                  />
                )}
              </div>
              <div
                className={cn(sizeSm ? "text-xs" : "text-sm")}
                style={{ color: textColor }}
              >
                {Array.isArray(processedDesc) ? (
                  processedDesc
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: purify(replaceAtomitags(descHtml, atomitags)),
                    }}
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      hyphens: "auto",
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "badge": {
      const template = component.content?.template || "garantia";
      const title = component.content?.title || "100%";
      const subtitle = component.content?.subtitle || "SEGURO";
      const description = component.content?.description || "";
      const primaryColor = component.content?.colorPrimary || "#22c55e";
      const titleColor = component.content?.titleColor || "#ffffff";
      const align = component.content?.align || "center";
      const width = component.content?.width || 200;
      const bgMode =
        component.content?.bgMode ||
        (component.content?.bgColor &&
        component.content.bgColor !== "transparent"
          ? "solid"
          : "none");
      const paddingBadge = component.content?.padding ?? 0;
      let badgeWrapperStyles = {
        padding: `${paddingBadge}px`,
        borderRadius: `${component.content?.borderRadius ?? 0}px`,
      };
      if (bgMode === "solid") {
        badgeWrapperStyles.backgroundColor =
          component.content?.bgColor || "#ffffff";
      } else if (bgMode === "gradient") {
        const startB = component.content?.gradientStartColor || "#ffffff";
        const endB = component.content?.gradientEndColor || "#f3f4f6";
        badgeWrapperStyles.backgroundImage = `linear-gradient(135deg, ${startB}, ${endB})`;
      } else {
        badgeWrapperStyles.backgroundColor = "transparent";
      }

      const renderBadge = () => {
        if (template === "privacy") {
          return (
            <svg
              viewBox="0 0 448 353"
              xmlns="http://www.w3.org/2000/svg"
              className="svg-privacy-render w-full h-auto"
            >
              <g stroke="black" strokeWidth="5" fill={primaryColor}>
                <path d="M406 244l35-24s-16-3-45-3-38 3-38 3l-48 53s29-8 65-8 65 8 65 8l-34-29z" />
                <path d="M42 244L7 220s16-3 45-3 38 3 38 3l48 53s-29-8-65-8-65 8-65 8l34-29z" />
                <path d="M93 238L42 215v-18h51v41z" />
                <path d="M359 238l51-23v-18h-51v41z" />
                <path
                  d="M229 3L115 37s-2 14-10 24c-8 10-33 18-33 18s-1 60 9 135c10 76 148 136 148 136s135-60 146-136c10-76 8-135 8-135s23-6-32-17c-9-10-10-26-10-26L229 3z"
                  fill="#fff"
                />
                <path
                  d="M229 16L124 47s-2 13-9 22-30 17-30 17-1 56 9 126c9 70 136 125 136 125s125-55 135-125c9-70 7-126 7-126s-21-6-30-15c-9-9-9-23-9-23L229 16z"
                  fill="none"
                />
                <path
                  d="M80 206h296l-2 18s-86-7-149-7-143 8-143 8l-2-19z"
                  fill="black"
                  opacity="0.2"
                />
                <path
                  d="M35 115h384l-8 101s-104-5-184-5-187 5-187 5l-5-101z"
                  fill={primaryColor}
                />
              </g>
              <g fill={primaryColor}>
                <path d="M197 64c.3-.9 1.5-.9 1.8 0l1 3c.1.3.3.6.6.8.2.2.5.2.7.2h3.4c.9 0 1.2 1.1.5 1.6l-2.7 2c-.3.2-.4.6-.3.9l1 3.1c.3.9-.7 1.6-1.4 1.1l-2.7-2c-.3-.2-.6-.2-.9 0l-2.7 2c-.7.5-1.7-.2-1.4-1.1l1-3.1c.1-.3 0-.7-.3-.9l-2.7-2c-.7-.5-.4-1.6.5-1.6h3.4c.3 0 .6-.1.8-.2.2-.2.4-.5.5-.8l1-3zm59 0c.3-.9 1.5-.9 1.8 0l1 3c.1.3.3.6.6.8.2.2.5.2.7.2h3.4c.9 0 1.2 1.1.5 1.6l-2.7 2c-.3.2-.4.6-.3.9l1 3.1c.3.9-.7 1.6-1.4 1.1l-2.7-2c-.3-.2-.6-.2-.9 0l-2.7 2c-.7.5-1.7-.2-1.4-1.1l1-3.1c.1-.3 0-.7-.3-.9l-2.7-2c-.7-.5-.4-1.6.5-1.6h3.4c.3 0 .6-.1.8-.2.2-.2.4-.5.5-.8l1-3z" />
              </g>
              <g fill="#000">
                <path d="M214 64c5-.1 10-2.2 14-6 4 4 9 5.9 14 6 .1.9.1 2 .1 3 0 9.9-6 18-14 21-8-3-14-11-14-21 0-1 .1-2 .2-3zm20 7c.3-.3.5-.8.5-1.3 0-.5-.2-1-.5-1.3-.3-.3-.8-.5-1.3-.5s-1 .2-1.3.5l-5.8 6.2-2.2-2.4c-.3-.3-.8-.5-1.3-.5s-1 .2-1.3.5c-.3.3-.5.8-.5 1.3s.2 1 .5 1.3l3.5 3.7c.3.3.8.5 1.3.5s1-.2 1.3-.5l7-7.8z" />
              </g>
              <text
                x="223"
                y="185"
                fontSize="55"
                fontWeight="bold"
                fill="#FFF"
                textAnchor="middle"
              >
                {title}
              </text>
              <text
                x="223"
                y="250"
                fontSize="22"
                fontWeight="bold"
                fill="black"
                textAnchor="middle"
              >
                {subtitle}
              </text>
            </svg>
          );
        } else if (template === "garantia") {
          return (
            <svg
              viewBox="0 0 472 361"
              xmlns="http://www.w3.org/2000/svg"
              className="svg-garantia-render w-full h-auto"
            >
              <path
                d="M427.103 244.562L464.382 225.083C464.382 225.083 449.138 220.621 420.605 216.5C392.073 212.38 382.587 214.756 382.587 214.756L328.647 261.29C328.647 261.29 358.48 257.65 393.687 262.199C428.894 266.748 457.753 277.59 457.753 277.59L427.103 244.562Z"
                fill={primaryColor}
                stroke="black"
                strokeWidth="5"
              />
              <path
                d="M44.1845 244.872L7.32182 224.615C7.32182 224.615 22.6568 220.473 51.2694 216.953C79.8821 213.433 89.3158 216.007 89.3158 216.007L142.267 263.663C142.267 263.663 112.518 259.398 77.2224 263.207C41.9272 267.015 12.8473 277.249 12.8473 277.249L44.1845 244.872Z"
                fill={primaryColor}
                stroke="black"
                strokeWidth="5"
              />
              <path
                d="M108.568 245L35.5684 212.278V188H108.568V245Z"
                fill={primaryColor}
                stroke="black"
                strokeWidth="5"
              />
              <path
                d="M363.568 245L432.568 211.704V187H363.568V245Z"
                fill={primaryColor}
                stroke="black"
                strokeWidth="5"
              />
              <mask id="path-5-inside-1" fill="white">
                <path d="M214.765 7.08462C227.686 -2.36154 245.239 -2.36154 258.175 7.08462C289.146 29.7374 282.482 27.5714 320.839 27.451C336.858 27.4059 351.057 37.7245 355.946 52.9617C367.678 89.4678 363.572 83.8272 394.678 106.269C407.659 115.64 413.089 132.337 408.095 147.559C396.152 183.914 396.092 176.935 408.095 213.441C413.104 228.648 407.674 245.36 394.678 254.731C363.572 277.158 367.693 271.517 355.946 308.038C351.057 323.29 336.843 333.609 320.839 333.549C282.467 333.429 289.131 331.263 258.175 353.915C245.254 363.362 227.701 363.362 214.765 353.915C183.794 331.278 190.458 333.414 152.101 333.549C136.082 333.594 121.883 323.275 116.994 308.038C105.262 271.487 109.308 277.128 78.2617 254.731C65.2808 245.36 59.8507 228.663 64.8446 213.441C76.8027 177.086 76.8478 184.065 64.8446 147.559C59.8357 132.322 65.2657 115.625 78.2467 106.254C109.263 83.8573 105.246 89.543 116.979 52.9467C121.868 37.6944 136.082 27.3758 152.086 27.436C190.367 27.5563 183.614 29.8577 214.765 7.08462Z" />
              </mask>
              <path
                d="M214.765 7.08462C227.686 -2.36154 245.239 -2.36154 258.175 7.08462C289.146 29.7374 282.482 27.5714 320.839 27.451C336.858 27.4059 351.057 37.7245 355.946 52.9617C367.678 89.4678 363.572 83.8272 394.678 106.269C407.659 115.64 413.089 132.337 408.095 147.559C396.152 183.914 396.092 176.935 408.095 213.441C413.104 228.648 407.674 245.36 394.678 254.731C363.572 277.158 367.693 271.517 355.946 308.038C351.057 323.29 336.843 333.609 320.839 333.549C282.467 333.429 289.131 331.263 258.175 353.915C245.254 363.362 227.701 363.362 214.765 353.915C183.794 331.278 190.458 333.414 152.101 333.549C136.082 333.594 121.883 323.275 116.994 308.038C105.262 271.487 109.308 277.128 78.2617 254.731C65.2808 245.36 59.8507 228.663 64.8446 213.441C76.8027 177.086 76.8478 184.065 64.8446 147.559C59.8357 132.322 65.2657 115.625 78.2467 106.254C109.263 83.8573 105.246 89.543 116.979 52.9467C121.868 37.6944 136.082 27.3758 152.086 27.436C190.367 27.5563 183.614 29.8577 214.765 7.08462Z"
                fill="white"
                stroke="black"
                strokeWidth="10"
                mask="url(#path-5-inside-1)"
              />
              <mask id="path-6-inside-2" fill="white">
                <path d="M216.717 25.6795C228.202 17.2722 243.804 17.2722 255.302 25.6795C282.83 45.841 276.907 43.9133 311 43.8062C325.238 43.766 337.859 52.9498 342.204 66.5113C352.633 99.0027 348.983 93.9824 376.631 113.956C388.169 122.297 392.996 137.157 388.557 150.705C377.941 183.063 377.888 176.851 388.557 209.342C393.009 222.877 388.182 237.75 376.631 246.091C348.983 266.052 352.646 261.031 342.204 293.536C337.859 307.111 325.225 316.295 311 316.241C276.894 316.134 282.817 314.206 255.302 334.368C243.817 342.775 228.215 342.775 216.717 334.368C189.189 314.22 195.112 316.121 161.019 316.241C146.781 316.281 134.16 307.097 129.815 293.536C119.386 261.004 122.983 266.025 95.388 246.091C83.85 237.75 79.0236 222.89 83.4623 209.342C94.0911 176.985 94.1312 183.196 83.4623 150.705C79.0102 137.144 83.8366 122.283 95.3746 113.943C122.943 94.0092 119.373 99.0696 129.801 66.4979C134.146 52.923 146.781 43.7392 161.006 43.7928C195.032 43.8999 189.029 45.9481 216.717 25.6795Z" />
              </mask>
              <path
                d="M216.717 25.6795C228.202 17.2722 243.804 17.2722 255.302 25.6795C282.83 45.841 276.907 43.9133 311 43.8062C325.238 43.766 337.859 52.9498 342.204 66.5113C352.633 99.0027 348.983 93.9824 376.631 113.956C388.169 122.297 392.996 137.157 388.557 150.705C377.941 183.063 377.888 176.851 388.557 209.342C393.009 222.877 388.182 237.75 376.631 246.091C348.983 266.052 352.646 261.031 342.204 293.536C337.859 307.111 325.225 316.295 311 316.241C276.894 316.134 282.817 314.206 255.302 334.368C243.817 342.775 228.215 342.775 216.717 334.368C189.189 314.22 195.112 316.121 161.019 316.241C146.781 316.281 134.16 307.097 129.815 293.536C119.386 261.004 122.983 266.025 95.388 246.091C83.85 237.75 79.0236 222.89 83.4623 209.342C94.0911 176.985 94.1312 183.196 83.4623 150.705C79.0102 137.144 83.8366 122.283 95.3746 113.943C122.943 94.0092 119.373 99.0696 129.801 66.4979C134.146 52.923 146.781 43.7392 161.006 43.7928C195.032 43.8999 189.029 45.9481 216.717 25.6795Z"
                stroke={primaryColor}
                strokeWidth="10"
                fill="white"
                mask="url(#path-6-inside-2)"
              />
              <path
                d="M69.5 204H403L406.5 221C406.5 221 302.694 215.038 230.97 215.038C159.246 215.038 65 221.5 65 221.5L69.5 204Z"
                fill="black"
                fillOpacity="0.2"
              />
              <mask id="path-8-inside-3" fill="white">
                <path d="M28 112H444L435.266 214C435.266 214 323.222 208.858 236.115 208.858C149.008 208.858 33.8608 214 33.8608 214L28 112Z" />
              </mask>
              <path
                d="M28 112H444L435.266 214C435.266 214 323.222 208.858 236.115 208.858C149.008 208.858 33.8608 214 33.8608 214L28 112Z"
                fill={primaryColor}
                stroke="black"
                strokeWidth="10"
                mask="url(#path-8-inside-3)"
              />
              <path
                d="M235 63.5869L238.668 71.1549L247 72.3059L240.936 78.1339L242.416 86.4129L235 82.4459L227.583 86.4129L229.064 78.1339L223 72.3059L231.332 71.1549L235 63.5869Z"
                fill="black"
              />
              <path
                d="M270.5 66.4648L273.404 72.4562L280 73.3674L275.199 77.9812L276.371 84.5354L270.5 81.3949L264.628 84.5354L265.801 77.9812L261 73.3674L267.596 72.4562L270.5 66.4648Z"
                fill="black"
              />
              <path
                d="M199.5 66.4648L202.404 72.4562L209 73.3674L204.199 77.9812L205.371 84.5354L199.5 81.3949L193.628 84.5354L194.801 77.9812L190 73.3674L196.596 72.4562L199.5 66.4648Z"
                fill="black"
              />
              <text
                transform="translate(245 188)"
                fontSize="70"
                fontWeight="bold"
                fill="#FFF"
                textAnchor="middle"
              >
                {title}
              </text>
              <text
                transform="translate(235 260)"
                fontSize="22"
                fontWeight="bold"
                fill="black"
                textAnchor="middle"
              >
                {subtitle}
              </text>
            </svg>
          );
        }
        // dias template
        return (
          <svg
            viewBox="0 0 448 347"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="svg-dias-render w-full h-auto"
          >
            <path
              d="M41.5769 245.82L7 221.803C7 221.803 22.6975 219.283 51.5469 218.769C80.3964 218.255 89.5179 221.803 89.5179 221.803L137.247 274.774C137.247 274.774 108.079 267.42 72.5476 267.523C37.0162 267.626 7 274.774 7 274.774L41.5769 245.82Z"
              fill={primaryColor}
              stroke="black"
              strokeWidth="5"
            />
            <path
              d="M406.013 245.871L440.59 221.854C440.59 221.854 424.893 219.334 396.043 218.82C367.194 218.306 358.072 221.854 358.072 221.854L310.343 274.825C310.343 274.825 339.511 267.471 375.042 267.574C410.574 267.677 440.59 274.825 440.59 274.825L406.013 245.871Z"
              fill={primaryColor}
              stroke="black"
              strokeWidth="5"
            />
            <path
              d="M377.158 236.412L409.826 213.029V195.681H377.158V236.412Z"
              fill={primaryColor}
              stroke="black"
              strokeWidth="5"
            />
            <path
              d="M74.2729 236.412L41.6052 213.029V195.681H74.2729V236.412Z"
              fill={primaryColor}
              stroke="black"
              strokeWidth="5"
            />
            <path
              d="M69.5249 116.247L69.5249 231.459C69.5249 249.488 79.2315 266.122 94.9287 274.991L198.39 333.448C213.257 341.848 231.384 342.08 246.462 334.062L358.011 274.742C374.334 266.062 384.535 249.083 384.535 230.596L384.535 117.112C384.535 98.7046 374.421 81.7857 358.207 73.0708L246.663 13.1179C231.48 4.95724 213.165 5.19259 198.196 13.7407L94.7294 72.8282C79.1442 81.7285 69.5249 98.2993 69.5249 116.247Z"
              fill="white"
              stroke="black"
              strokeWidth="5"
            />
            <path
              d="M86.9724 125.016L86.9724 221.769C86.9724 239.799 96.6794 256.432 112.377 265.302L199.587 314.575C214.455 322.975 232.581 323.206 247.658 315.189L341.942 265.053C358.265 256.373 368.467 239.394 368.467 220.906L368.467 125.881C368.467 107.473 358.352 90.5537 342.138 81.839L247.86 31.1682C232.677 23.0081 214.362 23.2435 199.394 31.791L112.178 81.5963C96.5921 90.4966 86.9724 107.068 86.9724 125.016Z"
              stroke={primaryColor}
              strokeWidth="5"
            />
            <path
              d="M71.0628 205.843H382.891V224.357C382.891 224.357 291.358 217.26 223.689 217.26C156.021 217.26 71.0627 224.357 71.0627 224.357L71.0628 205.843Z"
              fill="black"
              fillOpacity="0.2"
            />
            <mask id="path-8-inside-1" fill="white">
              <path d="M35.001 113.016H418.953L410.892 214.02C410.892 214.02 307.48 208.929 227.083 208.929C146.687 208.929 40.4102 214.02 40.4102 214.02L35.001 113.016Z" />
            </mask>
            <path
              d="M35.001 113.016H418.953L410.892 214.02C410.892 214.02 307.48 208.929 227.083 208.929C146.687 208.929 40.4102 214.02 40.4102 214.02L35.001 113.016Z"
              fill={primaryColor}
              stroke="black"
              strokeWidth="10"
              mask="url(#path-8-inside-1)"
            />
            <text
              transform="translate(225 100)"
              fontSize="55"
              fontWeight="bold"
              fill="black"
              textAnchor="middle"
            >
              {title}
            </text>
            <text
              transform="translate(225 185)"
              fontSize="65"
              fontWeight="bold"
              fill="#FFF"
              textAnchor="middle"
            >
              {subtitle}
            </text>
            <text
              transform="translate(223 255)"
              fontSize="20"
              fontWeight="bold"
              fill="black"
              textAnchor="middle"
            >
              {description}
            </text>
          </svg>
        );
      };

      return (
        <div
          className={cn("my-4", {
            "text-left": align === "left",
            "text-center": align === "center",
            "text-right": align === "right",
          })}
          style={badgeWrapperStyles}
        >
          <div
            className={cn("flex", {
              "justify-start": align === "left",
              "justify-center": align === "center",
              "justify-end": align === "right",
            })}
          >
            <div style={{ width: `${width}px`, maxWidth: "100%" }}>
              {renderBadge()}
            </div>
          </div>
        </div>
      );
    }

    case "list": {
      const items = component.content?.items || [
        { id: "1", text: "Item 1" },
        { id: "2", text: "Item 2" },
        { id: "3", text: "Item 3" },
      ];
      const iconColor = component.content?.iconColor || "#3B82F6";
      const bgMode =
        component.content?.bgMode ||
        (component.content?.bgColor &&
        component.content.bgColor !== "transparent"
          ? "solid"
          : "none");
      const listTextColor = component.content?.textColor || "#000000";
      const padding = component.content?.padding ?? 0;
      const align = component.content?.align || "left";
      const fontSize = component.content?.fontSize || 16;
      const iconKey = component.content?.icon || "check";

      const iconMap = {
        check: CheckCircle,
        x: X,
        circle: Circle,
        circledot: CircleDot,
        circlex: XCircleIcon,
        dot: Dot,
        chevronright: ChevronRight,
        arrowright: ArrowRight,
        sparkle: Sparkles,
      };
      const IconComponent = iconMap[iconKey] || CheckCircle;

      // Build background styles
      let backgroundStyles = {
        padding: `${padding}px`,
        borderRadius: `${component.content?.borderRadius ?? 0}px`,
      };
      if (bgMode === "solid") {
        backgroundStyles.backgroundColor =
          component.content?.bgColor || "#FFFFFF";
      } else if (bgMode === "gradient") {
        const start = component.content?.gradientStartColor || "#FFFFFF";
        const end = component.content?.gradientEndColor || "#F3F4F6";
        backgroundStyles.backgroundImage = `linear-gradient(135deg, ${start}, ${end})`;
      } else {
        backgroundStyles.backgroundColor = "transparent";
      }

      return (
        <div className="rounded-lg" style={backgroundStyles}>
          <ul className="space-y-2">
            {items.map((item) => (
              <li
                key={item.id}
                className={cn("flex items-start gap-2", {
                  "justify-start": align === "left",
                  "justify-center": align === "center",
                  "justify-end": align === "right",
                })}
                style={{ fontSize: `${fontSize}px` }}
              >
                <IconComponent
                  className="flex-shrink-0"
                  style={{
                    width: fontSize + 4,
                    height: fontSize + 4,
                    color: iconColor,
                  }}
                />
                <span
                  style={{
                    color: listTextColor,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    case "header": {
      const headerTitle = component.content?.title || "Nome do seu produto";
      const headerSubtitle = component.content?.subtitle || "";
      const headerAlign = component.content?.align || "left";
      const headerColor =
        component.content?.color || theme?.bodyText || "#000000";

      return (
        <div
          className={cn({
            "text-left": headerAlign === "left",
            "text-center": headerAlign === "center",
            "text-right": headerAlign === "right",
          })}
        >
          <h1
            className="text-2xl font-bold leading-tight"
            style={{ color: headerColor }}
          >
            {headerTitle}
          </h1>
          {headerSubtitle && (
            <p
              className="text-base mt-2"
              style={{ color: theme?.mutedText || "#6B7280" }}
            >
              {headerSubtitle}
            </p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

// Countdown Timer Component
const CountdownTimer = ({
  bgColor = "#E45C5C",
  textColor = "#FFFFFF",
  timerType = "minutes",
  duration = "00:15:00",
  endDate = "",
  activeText = "Oferta por tempo limitado",
  finishedText = "O tempo acabou!",
  fixed = false,
  size = "big",
}) => {
  const [now, setNow] = useState(Date.now());
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Size mappings
  const sizeConfig = {
    small: {
      padding: "p-2",
      timerText: "text-xl md:text-2xl",
      iconSize: 20,
      statusText: "text-xs md:text-sm",
      gap: "mr-2",
    },
    medium: {
      padding: "p-3",
      timerText: "text-2xl md:text-3xl",
      iconSize: 26,
      statusText: "text-sm md:text-base",
      gap: "mr-3",
    },
    big: {
      padding: "p-4",
      timerText: "text-3xl md:text-4xl",
      iconSize: 32,
      statusText: "text-base md:text-lg",
      gap: "mr-4",
    },
  };

  const currentSize = sizeConfig[size] || sizeConfig.big;

  let timerValue = "00:00";
  let expired = false;

  if (timerType === "datetime" && endDate) {
    const end = new Date(endDate).getTime();
    const diff = end - now;
    if (diff <= 0) {
      expired = true;
      timerValue = "00:00";
    } else {
      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      timerValue = `${
        hours > 0 ? String(hours).padStart(2, "0") + ":" : ""
      }${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
  } else {
    // Parse duration (format: HH:MM:SS)
    const [h, m, s] = duration.split(":").map(Number);
    const totalDuration = (h * 3600 + m * 60 + s) * 1000;
    const diff = totalDuration - (now - startTimeRef.current);
    if (diff <= 0) {
      expired = true;
      timerValue = "00:00";
    } else {
      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      timerValue = `${
        hours > 0 ? String(hours).padStart(2, "0") + ":" : ""
      }${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
  }

  return (
    <div
      className={cn(
        "w-full flex items-center justify-center",
        currentSize.padding,
        !fixed && "rounded-lg"
      )}
      style={{ backgroundColor: bgColor }}
    >
      <span
        className={cn(
          "font-bold text-white",
          currentSize.timerText,
          currentSize.gap
        )}
        style={{ color: textColor }}
      >
        {timerValue}
      </span>
      <span className="mr-3">
        <svg
          width={currentSize.iconSize}
          height={currentSize.iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke={textColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </span>
      <span
        className={cn("text-white font-normal", currentSize.statusText)}
        style={{ color: textColor }}
      >
        {expired ? finishedText : activeText}
      </span>
    </div>
  );
};

// Main checkout renderer component
export default function CheckoutRenderer({
  checkoutDesign,
  isMobile = false,
  theme,
}) {
  if (!checkoutDesign) return null;

  // Get components based on device type
  const components = isMobile
    ? checkoutDesign.mobileComponents || []
    : checkoutDesign.desktopComponents || [];

  // Get page settings based on device type
  const pageSettings = isMobile
    ? checkoutDesign.mobilePageSettings || {}
    : checkoutDesign.desktopPageSettings || {};

  // Group components by area
  const componentsByArea = {
    top: [],
    side: [],
    bottom: [],
  };

  // Only get root components (no parentId)
  components
    .filter((c) => !c.parentId)
    .forEach((component) => {
      const area = component.area || "top";
      if (componentsByArea[area]) {
        componentsByArea[area].push(component);
      }
    });

  return (
    <div
      className="checkout-design-renderer"
      style={{
        fontFamily: pageSettings.fontFamily || "inherit",
      }}
    >
      {/* Top area components */}
      {componentsByArea.top.length > 0 && (
        <div className="checkout-area-top mb-5">
          {componentsByArea.top.map((component) => (
            <ComponentRenderer
              key={component.id}
              component={component}
              allComponents={components}
              isMobile={isMobile}
              theme={theme}
            />
          ))}
        </div>
      )}

      {/* Side area components - will be handled differently in main layout */}
      {componentsByArea.side.length > 0 && (
        <div className="checkout-area-side mb-5">
          {componentsByArea.side.map((component) => (
            <ComponentRenderer
              key={component.id}
              component={component}
              allComponents={components}
              isMobile={isMobile}
              theme={theme}
            />
          ))}
        </div>
      )}

      {/* Bottom area components */}
      {componentsByArea.bottom.length > 0 && (
        <div className="checkout-area-bottom mt-5">
          {componentsByArea.bottom.map((component) => (
            <ComponentRenderer
              key={component.id}
              component={component}
              allComponents={components}
              isMobile={isMobile}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
}
